/**
 * What the editor tells a visitor when a template it was asked to open cannot
 * be loaded, from the HTTP status and the API's `detail` (a string, or an
 * object carrying a `code`).
 *
 *   signin       — no session, or the server rejected it: sign in, then return
 *   pro_only     — the existing upgrade modal
 *   purchase     — the existing marketplace purchase page
 *   unavailable / disabled / denied / error — a blocking message in place of the canvas
 */
export function describeTemplateLoadFailure(status, detail) {
  const code = detail && typeof detail === 'object' ? detail.code : null;

  if (status === 401) {
    return { kind: 'signin', title: 'Please sign in', message: 'Sign in to open this template.', canRetry: false };
  }
  if (status === 403 && (code === 'pro_only_template' || code === 'upgrade_required')) {
    return { kind: 'pro_only', title: 'Pro template', message: 'This template is available on the Pro plan.', canRetry: false };
  }
  if (status === 403 && code === 'purchase_required') {
    return {
      kind: 'purchase', templateId: detail.template_id || null,
      title: 'Purchase required', message: 'You need to purchase this template before using it.', canRetry: false,
    };
  }
  if (status === 403 && code === 'use_template_disabled') {
    return {
      kind: 'disabled', title: 'This template can’t be used right now',
      message: 'The template’s owner has turned off new uses.', canRetry: false,
    };
  }
  if (status === 403) {
    return {
      kind: 'denied', title: 'You don’t have access to this template',
      message: 'Your account doesn’t have permission to open it.', canRetry: false,
    };
  }
  if (status === 404) {
    return {
      kind: 'unavailable', title: 'This template isn’t available',
      message: 'It may have been removed, or it isn’t published yet. Check the link, or pick another template.',
      canRetry: false,
    };
  }
  return {
    kind: 'error', title: 'We couldn’t load this template',
    message: 'Something went wrong on our side or with the connection. Please try again.',
    canRetry: true,
  };
}
